/**
 * 種別設定の画面。
 *
 * 種別は Firestore に持つ（`kind_settings` コレクション）。以前は「いま出ているタスクの
 * 種別」を数えて出していたので、まだタスクの無い種別を置けず、直しても残らなかった。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 種別設定の画面。チームごとの種別と表示色を Firestore に足す・直す・消す
 * @graph-connects useSettings [calls] 設定の読み書き
 * @graph-connects filterByTeam [calls] チームごとのタスクに絞る
 * @graph-connects kindHex [calls] 種別の既定色を引く
 */
import { useState } from 'react';
import { useSettings } from '@/api/use-settings';
import type { SettingEntry } from '@/api/client';
import { kindHex } from '@/components/KindLabel';
import { filterByTeam, TEAM_IDS, type Task, type TeamId } from '@/tasks/task';

/**
 * チームの表示名。
 * @graph-connects none
 */
const TEAM_LABEL: Record<TeamId, { label: string; fullName: string }> = {
  sft: { label: 'SFT', fullName: 'ストラテジックファイナンスチーム' },
  cpt: { label: 'CPT', fullName: 'コーポレートサポートチーム' },
};

/** 新しい種別の既定色 */
/** @graph-connects none */
export const NEW_KIND_COLOR = '#9c9289';

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business まだ登録していない種別を、いまのタスクから拾う。最初の取り込みに使う
 * @graph-connects filterByTeam [calls] チームごとのタスクに絞る
 * @graph-connects kindHex [calls] 種別の既定色を引く
 */
export function missingKinds(
  tasks: readonly Task[],
  team: TeamId,
  settings: readonly SettingEntry[],
): { name: string; color: string }[] {
  const stored = new Set(
    settings.filter((entry) => entry.team === team).map((entry) => entry.name),
  );
  return [...new Set(filterByTeam(tasks, team).map((task) => task.kind))]
    .filter((name) => !stored.has(name))
    .sort((a, b) => a.localeCompare(b, 'ja'))
    .map((name) => ({ name, color: kindHex(name) }));
}

/** @graph-connects none */
const SMALL_BUTTON =
  'rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)]';

/**
 * KindsSettings の props。
 */
export interface KindsSettingsProps {
  /** 件数を数えるタスク */
  tasks: readonly Task[];
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 種別設定の画面。チームごとに種別と表示色を足す・直す・消す
 * @graph-connects initialKinds [calls] いまのタスクから初期設定を作る
 * @graph-connects filterByTeam [calls] チームごとのタスクに絞る
 */
export function KindsSettings({ tasks }: KindsSettingsProps) {
  const { settings, loading, error, add, update, remove } = useSettings('kind-settings');
  const [editingId, setEditingId] = useState('');
  const [draft, setDraft] = useState<SettingEntry | null>(null);

  const startEdit = (setting: SettingEntry) => {
    setEditingId(setting.id);
    setDraft({ ...setting });
  };

  const save = () => {
    if (draft === null || draft.name.trim() === '') {
      return;
    }
    void update(draft.id, { name: draft.name.trim(), color: draft.color });
    setEditingId('');
    setDraft(null);
  };

  return (
    <main className="min-w-0 flex-1 overflow-y-auto px-8 py-5">
      <header className="mb-4">
        <h1 className="m-0 text-lg font-semibold tracking-tight text-[var(--color-text-primary)]">
          種別設定
        </h1>
        <p className="mt-0.5 mb-0 text-xs text-[var(--color-text-secondary)]">
          チームごとのタスク種別と表示色を管理する。直した内容はそのまま保存されます
        </p>
      </header>

      {error === '' ? null : (
        <p className="mb-3 max-w-2xl rounded border border-[var(--color-doing-border)] bg-[var(--color-doing-bg)] px-4 py-2 text-sm text-[var(--color-doing-text)]">
          {error}
        </p>
      )}

      {/* 読み込み中の知らせを出すのは、まだ出せる一覧が無いときだけ。書いたあとの
          読み直しでも出すと、一覧が 1 行に縮んでスクロール位置が先頭へ戻ってしまう */}
      {loading && settings.length === 0 ? (
        <p className="py-16 text-center text-sm text-[var(--color-text-muted)]">
          読み込んでいます…
        </p>
      ) : (
        <div className="max-w-2xl space-y-8">
          {TEAM_IDS.map((team) => {
            const teamTasks = filterByTeam(tasks, team);
            const teamKinds = settings.filter((kind) => kind.team === team);
            const missing = missingKinds(tasks, team, settings);

            return (
              <section key={team}>
                <h2 className="m-0 mb-1 text-sm font-semibold text-[var(--color-text-primary)]">
                  {TEAM_LABEL[team].label}
                </h2>
                <p className="mt-0 mb-3 text-xs text-[var(--color-text-muted)]">
                  {TEAM_LABEL[team].fullName}
                </p>

                <table className="w-full border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)]">
                      <th className="py-2 pr-3 font-medium text-[var(--color-text-muted)]">種別</th>
                      <th className="w-24 py-2 pr-3 font-medium text-[var(--color-text-muted)]">
                        表示色
                      </th>
                      <th className="w-16 py-2 pr-3 text-right font-medium text-[var(--color-text-muted)]">
                        件数
                      </th>
                      <th className="w-32 py-2">
                        <span className="sr-only">操作</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamKinds.map((setting) => {
                      const editing = editingId === setting.id && draft !== null;
                      return (
                        <tr
                          key={setting.id}
                          className="border-b border-[var(--color-border-subtle)]"
                        >
                          <td className="py-2 pr-3">
                            {editing ? (
                              <input
                                type="text"
                                aria-label="種別名"
                                value={draft.name}
                                onChange={(event) => {
                                  setDraft({ ...draft, name: event.target.value });
                                }}
                                className="w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2 py-1 text-sm text-[var(--color-text-primary)]"
                              />
                            ) : (
                              <span
                                className="inline-block rounded px-1.5 py-0.5 text-xs whitespace-nowrap"
                                style={{
                                  backgroundColor: `${setting.color ?? NEW_KIND_COLOR}22`,
                                  color: setting.color ?? NEW_KIND_COLOR,
                                }}
                              >
                                {setting.name}
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-3">
                            {editing ? (
                              <input
                                type="color"
                                aria-label="表示色"
                                value={draft.color ?? NEW_KIND_COLOR}
                                onChange={(event) => {
                                  setDraft({ ...draft, color: event.target.value });
                                }}
                                className="h-7 w-12 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)]"
                              />
                            ) : (
                              <span
                                aria-hidden="true"
                                className="inline-block h-4 w-8 rounded border border-[var(--color-border-subtle)]"
                                style={{ backgroundColor: setting.color ?? NEW_KIND_COLOR }}
                              />
                            )}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums text-[var(--color-text-primary)]">
                            {teamTasks.filter((task) => task.kind === setting.name).length}
                          </td>
                          <td className="py-2">
                            <span className="flex justify-end gap-1">
                              {editing ? (
                                <>
                                  <button type="button" onClick={save} className={SMALL_BUTTON}>
                                    保存
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingId('');
                                      setDraft(null);
                                    }}
                                    className={SMALL_BUTTON}
                                  >
                                    戻る
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    aria-label={`${setting.name}を編集`}
                                    onClick={() => {
                                      startEdit(setting);
                                    }}
                                    className={SMALL_BUTTON}
                                  >
                                    編集
                                  </button>
                                  <button
                                    type="button"
                                    aria-label={`${setting.name}を削除`}
                                    onClick={() => {
                                      void remove(setting.id);
                                    }}
                                    className="rounded border border-[var(--color-border-subtle)] px-2 py-0.5 text-xs text-[var(--color-overdue)]"
                                  >
                                    削除
                                  </button>
                                </>
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      void add({ team, name: '新しい種別', color: NEW_KIND_COLOR });
                    }}
                    className="cursor-pointer rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-1.5 text-sm text-[var(--color-text-secondary)]"
                  >
                    {TEAM_LABEL[team].label} に種別を追加
                  </button>

                  {/* タスクに出てくるのに登録されていない種別は、まとめて入れられるようにする */}
                  {missing.length === 0 ? null : (
                    <button
                      type="button"
                      onClick={() => {
                        // 同じ一覧を見たまま同時に足すので、並び順はここで連番にする
                        const base = settings.length + 1;
                        for (const [index, kind] of missing.entries()) {
                          void add({
                            team,
                            name: kind.name,
                            color: kind.color,
                            order: base + index,
                          });
                        }
                      }}
                      className="cursor-pointer rounded bg-[var(--color-accent)] px-3 py-1.5 text-sm font-medium text-[var(--color-bg-surface)]"
                    >
                      タスクの種別 {missing.length} 件を取り込む
                    </button>
                  )}
                </div>
              </section>
            );
          })}

          <p className="mt-0 mb-0 text-xs leading-relaxed text-[var(--color-text-muted)]">
            件数はいま表示しているタスクから数えています。
          </p>
        </div>
      )}
    </main>
  );
}
